import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import SlideOverHeader from 'components/SlideOver/component/header';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import Banner from 'components/banner/banner';
import {
  COMPANY_ADMIN_ROLE,
  companyRoleDescription,
  companyRoleOptions,
} from 'constants/companyRoles';
import { validateContactForm } from 'helper/companyContactForm';
import { toPhoneValue } from 'helper/companyContactPhone';
import {
  getPerimeterErrorMessage,
  isCanceledRequest,
  isStalePerimeterError,
} from 'components/perimeter/perimeterErrors';
import {
  formatMemberName,
  haveSameMemberIds,
  isUnrestrictedRole,
} from 'components/perimeter/perimeterMembers';
import {
  MEMBER_PERIMETERS_PICKED,
  PERIMETER_MODALS,
} from 'components/perimeter/perimeterUsage';
import { fillText, perimeterText } from 'components/perimeter/perimeterText';
import CompanyContactPerimeters from './CompanyContactPerimeters';
import CompanyContactNotifications from './CompanyContactNotifications';
import useMemberNotificationsDraft, { NOTIFICATION_ERROR_KEYS } from './useMemberNotificationsDraft';
import useContactCrmFields from './useContactCrmFields';
import FieldLabel from 'components/styledInput/FieldLabel';
import { SkeletonLine } from 'components/skeleton/Skeleton';

// The lead statuses of a contact: none, or « Ambassadeur ».
export const LEAD_STATUSES = ['', 'ambassadeur'];

// errorCode from the api → [field to highlight, message key]
const API_ERRORS = {
  EMAIL_INVALID: ['email', 'emailInvalid'],
  EMAIL_ALREADY_USED: ['email', 'emailTaken'],
  PHONE_INVALID: ['phone', 'phoneInvalid'],
  FIRSTNAME_INVALID: ['firstName', 'required'],
  LASTNAME_INVALID: ['lastName', 'required'],
  ROLE_INVALID: ['role', 'required'],
  ADMIN_ROLE_TAKEN: ['role', 'adminTaken'],
};

const PENDING_VALIDATION = 0;

// The default texts speak of one perimeter being edited; here the request
// carries the member's whole list.
const MEMBER_PERIMETER_ERROR_OVERRIDES = {
  PERIMETER_MEMBER_NOT_RESTRICTABLE: 'member.errors.notRestrictable',
  PERIMETER_NOT_FOUND: 'member.errors.notFound',
  INVALID_PERIMETER_ID: 'member.errors.notFound',
  PERIMETER_ARCHIVED: 'member.errors.archived',
};

// errorCode of the notifications route → text key under notifications.errors.
// MEMBER_NOT_FOUND closes the screen instead, as for the perimeters.

const NO_PERIMETERS = { status: 'loading', active: [] };

// What the screen edits (`scope`): everything (the company panel's list), the
// person only (their contact page's « Informations »: name, e-mail, phone,
// position, lead status) or their place at this company only (role, perimeters,
// notifications).
export const EDIT_SCOPES = { all: 'all', person: 'person', company: 'company' };

const CompanyContactEditView = ({
  // The company's id, known before `companyInfo` is read (null until then).
  companyId: companyIdProp,
  // The company's name, for the description, until `companyInfo` is read.
  companyName,
  companyInfo,
  member,
  perimeters = NO_PERIMETERS,
  scope = EDIT_SCOPES.all,
  onBack,
  onSaved,
}) => {
  const showsPerson = scope !== EDIT_SCOPES.company;
  const showsCompany = scope !== EDIT_SCOPES.person;
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const t = (key) => language.getValue(`companyContacts.edit.${key}`, '');
  // A stored number the field cannot read shows empty; left untouched, it is
  // never sent, so it cannot block saving the rest.
  const [initialPhone] = useState(() => toPhoneValue(member.user?.phone) ?? '');
  const [form, setForm] = useState({
    firstName: member.user?.firstname ?? '',
    lastName: member.user?.lastname ?? '',
    email: member.user?.email ?? '',
    phone: initialPhone,
    role: String(member.role ?? ''),
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // null until the admin touches the list: it then follows the loaded one.
  const [draftPerimeterIds, setDraftPerimeterIds] = useState(null);
  const [perimeterError, setPerimeterError] = useState('');
  // The answer of the last contact save and what it sent: after a failed
  // perimeter save, a retry with the same values only sends the perimeters.
  const lastSavedContact = useRef(null);

  const memberId = String(member.user?._id ?? member.id_employee ?? '');
  const companyId = companyInfo?._id ?? companyIdProp;
  const crmFields = useContactCrmFields(companyId, memberId);
  const isPerimetersReady = perimeters.status === 'ready';
  const activePerimeters = useMemo(
    () => (isPerimetersReady ? perimeters.active : []),
    [isPerimetersReady, perimeters.active],
  );
  // No route gives a member's perimeters: they are the active perimeters that
  // list the member's account.
  const currentPerimeterIds = useMemo(
    () =>
      activePerimeters
        .filter((perimeter) =>
          (perimeter.members || []).some(
            (entry) => String(entry.id) === memberId,
          ),
        )
        .map((perimeter) => perimeter.id),
    [activePerimeters, memberId],
  );
  const selectedPerimeterIds = draftPerimeterIds ?? currentPerimeterIds;
  // In the order of the active list (sorted by name by the api); an id whose
  // perimeter is no longer active drops out, here and from the request.
  const selectedPerimeters = activePerimeters.filter((perimeter) =>
    selectedPerimeterIds.includes(perimeter.id),
  );
  const isUnrestricted = isUnrestrictedRole(form.role);

  // Judged on the perimeters block as shown, draft included; unknown (null)
  // until the perimeters are read. It only picks the hint: a member without a
  // perimeter keeps 'perimeter', which the api reads as 'all' until they are
  // attached to one (card 1306).
  const hasActivePerimeter = isPerimetersReady
    ? selectedPerimeters.length > 0
    : null;
  // A Director or an Administrator is never restricted: the table shows
  // 'all' where the api stores 'perimeter' (useMemberNotificationsDraft).
  const isPerimeterLevelBlocked = isUnrestricted;
  const notifications = useMemberNotificationsDraft(companyId, member.user?._id, isPerimeterLevelBlocked);
  const notificationHint = () => {
    if (isUnrestricted) return t('notifications.unrestrictedHint');
    if (hasActivePerimeter === false) return t('notifications.noPerimeterHint');
    return '';
  };


  useEffect(() => {
    if (modalDataReturn?.type !== MEMBER_PERIMETERS_PICKED) return;
    const pickedIds = modalDataReturn.perimeterIds || [];
    setModalDataReturn({});
    setPerimeterError('');
    setDraftPerimeterIds((previous) => {
      const base = previous ?? currentPerimeterIds;
      return [...base, ...pickedIds.filter((id) => !base.includes(id))];
    });
  }, [modalDataReturn, setModalDataReturn, currentPerimeterIds]);

  const openAddPerimeters = () => {
    setModalData({
      perimeters: activePerimeters.filter(
        (perimeter) => !selectedPerimeterIds.includes(perimeter.id),
      ),
      companyName: companyInfo?.name_commercial,
      memberName: formatMemberName(member.user),
    });
    toggle(PERIMETER_MODALS.addToMember);
  };

  const removePerimeter = (id) => {
    setPerimeterError('');
    setDraftPerimeterIds(selectedPerimeterIds.filter((kept) => kept !== id));
  };

  // The api refuses a second administrator whatever the other entry is, one
  // whose account is gone included; the warnings only count administrators
  // with an account, since an entry without one receives no reminder.
  const otherAdmins = useMemo(
    () =>
      (companyInfo?.employees ?? []).filter(
        (entry) =>
          Number(entry.role) === COMPANY_ADMIN_ROLE &&
          entry.user?._id !== member.user?._id,
      ),
    [companyInfo, member],
  );
  const otherAdminWithAccount = otherAdmins.find((entry) => entry.user);
  const isCompanyAdmin = Number(member.role) === COMPANY_ADMIN_ROLE;
  const normalizedEmail = form.email.trim().toLowerCase();
  // The warning is about the last administrator: with another one left, the
  // reminders of the company still reach someone.
  const isLastAdminLeaving =
    isCompanyAdmin &&
    !otherAdminWithAccount &&
    Number(form.role) !== COMPANY_ADMIN_ROLE;
  const isEmailChanged = normalizedEmail !== (member.user?.email ?? '');
  const isPending = member.user?.validation === PENDING_VALIDATION;

  // Never a message with an empty name: the holder can be an entry without an
  // account, or unknown to this screen when only the api saw them.
  const adminTakenText = () => {
    const name = [
      otherAdminWithAccount?.user?.firstname,
      otherAdminWithAccount?.user?.lastname,
    ]
      .filter(Boolean)
      .join(' ');
    if (name) return t('errors.adminTaken').replace('{name}', name);
    if (otherAdmins.length) return t('errors.adminTakenByMissingAccount');
    return t('errors.adminTakenUnknown');
  };

  const errorText = (key) => {
    if (!key) return undefined;
    if (key === 'adminTaken') return adminTakenText();
    return t(`errors.${key}`);
  };

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  // The phone field hands the number (E.164, '' when emptied), not an event.
  const updatePhone = (value) => {
    setForm((previous) => ({ ...previous, phone: value }));
    setErrors((previous) => ({ ...previous, phone: undefined }));
  };

  // The invitation warning belongs to the contact save: a retry that only
  // resends the perimeters must not show it a second time.
  const warnInvitationOnce = (answer) => {
    const saved = lastSavedContact.current;
    if (answer.invitationResent !== false || saved?.isInvitationWarned) {
      return false;
    }
    if (saved?.answer === answer) saved.isInvitationWarned = true;
    toast.warning(t('savedInvitationFailed'));
    return true;
  };

  const finishSave = (answer) => {
    const done = () => {
      // Saved either way; false means the old link is dead and the new one
      // never left, which the admin can fix with the resend action.
      if (!warnInvitationOnce(answer)) toast.success(t('saved'));
      onSaved(answer.employee);
    };
    if (!crmFields.changed) {
      done();
      return;
    }
    // The position and the lead status are the CRM's: written last, on the
    // e-mail the api kept.
    crmFields
      .save(answer.employee?.email ?? normalizedEmail)
      .catch((err) => {
        console.error('[CompanyContactEditView] crm fields', err?.response?.status);
        toast.error('Contact enregistré, mais son poste ou son statut du lead n’a pas été modifié.');
      })
      .finally(done);
  };

  // The contact stays saved: the panel takes it in, the screen stays open on
  // the draft and says the perimeters did not follow.
  const keepOpenWithPerimeterError = (answer, reason) => {
    warnInvitationOnce(answer);
    onSaved(answer.employee, { keepOpen: true });
    setPerimeterError(
      fillText(perimeterText(language, 'member.saveFailed'), { reason }),
    );
  };

  // Runs once the perimeters are written, so that the levels the admin chose
  // are the last write.
  const saveNotifications = (answer) => {
    const levels = notifications.patch;
    if (Object.keys(levels).length === 0) {
      setIsSaving(false);
      finishSave(answer);
      return;
    }
    ApiDatabase.setAdminMemberNotifications(
      { idCompany: companyInfo._id, idEmployee: member.user._id, levels },
      (response) => {
        setIsSaving(false);
        notifications.markSaved(response?.data);
        finishSave(answer);
      },
      (err) => {
        setIsSaving(false);
        if (isCanceledRequest(err)) return;
        console.error(err);
        const errorCode = err?.response?.data?.errorCode;
        if (errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        warnInvitationOnce(answer);
        onSaved(answer.employee, { keepOpen: true });
        const reasonKey = NOTIFICATION_ERROR_KEYS[errorCode] || 'generic';
        notifications.setError(
          fillText(t('notifications.saveFailed'), {
            reason: t(`notifications.errors.${reasonKey}`),
          }),
        );
      },
    );
  };

  // Runs once the contact is saved, so a Director or an Administrator is
  // judged on the role the api kept: those are never restricted, and the api
  // refuses them a list.
  const savePerimeters = (answer) => {
    // The person only: their place at the company is not on this screen.
    if (!showsCompany) {
      setIsSaving(false);
      finishSave(answer);
      return;
    }
    const savedRole = answer.employee?.role ?? form.role;
    const perimeterIds = selectedPerimeters.map((perimeter) => perimeter.id);
    if (isUnrestrictedRole(savedRole)) {
      saveNotifications(answer);
      return;
    }
    // The lists failed to reload after the admin changed the draft: nothing
    // can be compared nor sent, which must not read as a full success.
    if (!isPerimetersReady && draftPerimeterIds !== null) {
      setIsSaving(false);
      keepOpenWithPerimeterError(
        answer,
        perimeterText(language, 'member.errors.notLoaded'),
      );
      perimeters.reload?.();
      return;
    }
    if (
      !isPerimetersReady ||
      haveSameMemberIds(perimeterIds, currentPerimeterIds)
    ) {
      saveNotifications(answer);
      return;
    }
    ApiDatabase.setAdminMemberPerimeters(
      { idCompany: companyInfo._id, idEmployee: member.user._id, perimeterIds },
      () => {
        perimeters.reload();
        saveNotifications(answer);
      },
      (err) => {
        setIsSaving(false);
        if (isCanceledRequest(err)) return;
        console.error(err);
        if (err?.response?.data?.errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        keepOpenWithPerimeterError(
          answer,
          getPerimeterErrorMessage(
            language,
            err,
            MEMBER_PERIMETER_ERROR_OVERRIDES,
          ),
        );
        if (isStalePerimeterError(err)) perimeters.reload();
      },
    );
  };

  const save = () => {
    const clientErrors = validateContactForm(form, {
      // A legacy company can have two administrators and the api lets each of
      // them keep the role: an administrator keeping it adds none, so only
      // someone else taking the role is refused.
      otherAdminExists: otherAdmins.length > 0 && !isCompanyAdmin,
    });
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    const employee = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: normalizedEmail,
      role: Number(form.role),
    };
    // Sent only when changed: the api would refuse an old number it cannot
    // normalize, and that must not block saving the name.
    if (showsPerson && form.phone !== initialPhone) employee.phone = form.phone;
    const sentContact = JSON.stringify(employee);
    setIsSaving(true);
    setPerimeterError('');
    notifications.setError('');
    if (lastSavedContact.current?.sent === sentContact) {
      savePerimeters(lastSavedContact.current.answer);
      return;
    }
    ApiDatabase.postEditCompanyMember(
      {
        idCompany: companyInfo._id,
        idEmployee: member.user._id,
        employee,
      },
      (data) => {
        lastSavedContact.current = { sent: sentContact, answer: data };
        savePerimeters(data);
      },
      (err) => {
        setIsSaving(false);
        if (err?.name === 'CanceledError') return;
        const errorCode = err?.response?.data?.errorCode;
        // Removed meanwhile: retrying cannot succeed, so the screen closes.
        if (errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        const known = API_ERRORS[errorCode];
        if (known) {
          setErrors({ [known[0]]: known[1] });
          return;
        }
        toast.error(t('errors.generic'));
      },
    );
  };

  return (
    <>
      {/* The task panel's header: a title, what it edits, the cross that closes. */}
      <SlideOverHeader
        hide={onBack}
        closeLabel={t('back')}
        description={
          scope === EDIT_SCOPES.person
            ? t('personSubtitle')
            : scope === EDIT_SCOPES.company
              ? fillText(t('companySubtitle'), {
                  company: companyInfo?.name_commercial ?? companyName ?? '',
                }).trim()
              : t('subtitle')
        }
      >
        {t('title')}
      </SlideOverHeader>
      <SlideOverBody>
        <div className={'flex flex-col gap-4 text-sm'}>
          {showsPerson && (
            <>
              <div className={'grid grid-cols-2 gap-3'}>
                <StyledInput
                  type={'text'}
                  label={t('firstName')}
                  value={form.firstName}
                  onChange={update('firstName')}
                  error={errorText(errors.firstName)}
                />
                <StyledInput
                  type={'text'}
                  label={t('lastName')}
                  value={form.lastName}
                  onChange={update('lastName')}
                  error={errorText(errors.lastName)}
                />
              </div>
              <div className={'flex flex-col gap-1'}>
                <StyledInput
                  type={'email'}
                  label={t('email')}
                  value={form.email}
                  onChange={update('email')}
                  error={errorText(errors.email)}
                />
                {isEmailChanged && !errors.email ? (
                  <div className={'text-gray-500 text-xs'}>
                    {t(isPending ? 'pendingEmailChange' : 'activeEmailHint')}
                  </div>
                ) : null}
              </div>
              <StyledInput
                type={'phone'}
                label={t('phone')}
                value={form.phone}
                onChange={updatePhone}
                error={errorText(errors.phone)}
              />
              {crmFields.status === 'loading' ? (
                // Not known yet (the company panel's list): their place, not empty fields.
                [language.getValue('record.fieldPosition', 'Poste'), language.getValue('record.fieldLead', 'Statut du lead')].map((label) => (
                  <div key={label} className={'flex flex-col gap-1.5'} aria-busy={'true'}>
                    <FieldLabel label={label} />
                    <SkeletonLine className={'h-10 w-full'} />
                  </div>
                ))
              ) : (
                <>
                  <StyledInput
                    type={'text'}
                    label={language.getValue('record.fieldPosition', 'Poste')}
                    value={crmFields.position}
                    isDisabled={!crmFields.canEditPosition}
                    hint={
                      crmFields.status === 'ready' && !crmFields.canEditPosition
                        ? 'Le poste est écrit par la direction d’agence et les commerciaux.'
                        : null
                    }
                    onChange={(event) => crmFields.setPosition(event.target.value)}
                  />
                  <StyledInput
                    type={'select'}
                    label={language.getValue('record.fieldLead', 'Statut du lead')}
                    value={crmFields.leadStatus}
                    isDisabled={crmFields.status !== 'ready'}
                    list={LEAD_STATUSES.map((status) => ({
                      value: status,
                      label: language.getValue(`record.lead_${status || 'none'}`, ''),
                    }))}
                    onChange={(event) => crmFields.setLeadStatus(event.target.value)}
                  />
                </>
              )}
            </>
          )}
          {showsCompany && (
            <>
              <StyledInput
                type={'select'}
                label={t('role')}
                list={companyRoleOptions(language)}
                value={form.role}
                onChange={update('role')}
                error={errorText(errors.role)}
                hint={companyRoleDescription(language, form.role)}
              />
              {isLastAdminLeaving ? (
                <Banner type={'warning'}>{t('lastAdminWarning')}</Banner>
              ) : null}
              <CompanyContactPerimeters
                status={perimeters.status}
                selectedPerimeters={selectedPerimeters}
                isUnrestricted={isUnrestricted}
                error={perimeterError}
                onAdd={openAddPerimeters}
                onRemove={removePerimeter}
              />
              <CompanyContactNotifications
                status={notifications.status}
                levels={notifications.shownLevels}
                isPerimeterColumnDisabled={isPerimeterLevelBlocked}
                perimeterHint={notificationHint()}
                error={notifications.error}
                onChange={notifications.change}
                onRetry={notifications.reload}
              />
            </>
          )}
        </div>
      </SlideOverBody>
      <SlideOverFooter>
        <Button size={'SM'} color={'SECONDARY'} onClick={onBack}>
          {t('cancel')}
        </Button>
        {/* Shown before the company is read (a contact page's opening): the
            save waits for it, its rules need the other members. */}
        <Button
          size={'SM'}
          color={isSaving || !companyInfo ? 'DISABLED' : 'PRIMARY'}
          onClick={isSaving || !companyInfo ? undefined : save}
        >
          {isSaving ? t('saving') : t('save')}
        </Button>
      </SlideOverFooter>
    </>
  );
};

export default CompanyContactEditView;
